import * as React from "react";
import { Img } from "../../components/Img.js";
import { ArrowIcon, type DiagnosticCta } from "./cta.js";
import type { TestLandingConfig } from "./config.js";
import { leadMagnetForSlug } from "../../lib/lead-magnets.js";
import { seoPhraseFor } from "./seoOverrides.js";

/**
 * The two arms of the test-landing bridge-framing experiment (fun vs
 * competition). One component, two modes — the axis is the competitive FRAMING,
 * so the shell is shared (the two-column hero grid, the single in-place CTA via
 * `useStartDiagnostic`, the same button label, the dark "How it works" results
 * band, no social proof / FAQ / closer). What diverges: the hero copy, the
 * bridge copy, and the right-column visual — fun-bridge fans a collage of
 * live-game thumbnails; competition shows a mock live leaderboard (ranks +
 * beat-your-friends made concrete) so the arms read as visibly different.
 *
 *  - `fun-bridge`         — the proven baseline (prior experiment's winner).
 *                           Sells the game: "The math game kids actually want to
 *                           play." + "Fun in the game. Results on the {test}."
 *  - `competition-bridge` — reframes the same layout around competing: live
 *                           head-to-head battles, climbing the ranks/leaderboard,
 *                           beating friends. "The math game kids battle to win."
 *                           + "Compete in the game. Results on the {test}."
 *
 * Copy is derived from config fields (`shortName`, `gradeRange`, `heroTag`,
 * `heroMeta`) so the one template stays pooled across all ~65 slugs — there is
 * no `{test}` token system and none should be added.
 *
 * Stripe rule (design system §2.1): the rotated orange block wraps ONE short
 * word. A multi-word stripe line-breaks inside the box at display sizes and
 * turns into a giant orange slab — don't.
 */
export type LeanVariant = "fun-bridge" | "competition-bridge";

/**
 * Hero art shared by both arms: live games from LandingRoute's seed set, whose
 * thumbnails ship in every environment at a stable URL. Deliberately NOT
 * /game-covers/ — those depict coming-soon games, and the whole pitch is what
 * kids will actually be playing today.
 */
const GAME_SHOTS = [
  { slug: "blaster-gate", title: "Blaster Gate" },
  { slug: "math-slice-dojo", title: "Math Slice Dojo" },
  { slug: "math-crush", title: "Math Crush" },
] as const;

/**
 * The competition arm's hero visual: a mock live leaderboard that REPLACES the
 * fun arm's game-thumbnail collage, so the two arms read as visibly different
 * surfaces (the fun arm shows what kids play; this shows where they rank). The
 * standings are illustrative, not live data — your child sits near the top and
 * climbing, which is the arm's "ranks + beat your friends" pitch made concrete.
 */
const LEADERBOARD = [
  { rank: 1, name: "Maya R.", score: 980, you: false },
  { rank: 2, name: "Your student", score: 940, you: true },
  { rank: 3, name: "Liam T.", score: 910, you: false },
  { rank: 4, name: "Ava P.", score: 875, you: false },
  { rank: 5, name: "Noah K.", score: 840, you: false },
] as const;

function CompetitionBoard({ shortName }: { shortName: string }): React.ReactElement {
  return (
    <div
      className="tl-comp__board"
      role="img"
      aria-label={`Live leaderboard of kids competing on ${shortName} math skills — your student is ranked 2nd and climbing.`}
    >
      <div className="tl-comp__board-head">
        <span className="tl-comp__live">
          <span className="tl-comp__live-dot" aria-hidden="true" />
          Live leaderboard
        </span>
        <span className="tl-comp__board-sub">{shortName} skills · this week</span>
      </div>
      <ol className="tl-comp__rows">
        {LEADERBOARD.map((r) => (
          <li key={r.rank} className={`tl-comp__row${r.you ? " tl-comp__row--you" : ""}`}>
            <span className="tl-comp__rank">{r.rank}</span>
            <span className="tl-comp__name">{r.name}</span>
            <span className="tl-comp__score">
              {r.you && (
                <span className="tl-comp__up" aria-hidden="true">
                  ▲
                </span>
              )}
              {r.score}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

type BridgeStep = { n: string; title: string; desc: string };

export function LeanBody({
  config,
  variant,
  diagnostic,
  asH1 = true,
}: {
  config: TestLandingConfig;
  variant: LeanVariant;
  diagnostic: DiagnosticCta;
  /**
   * Render the hero title as `<h1>` (default) or a visually-identical `<p>`.
   * The SSR path stacks both experiment arms for the pre-paint gate; passing
   * `asH1={false}` to the non-control arm keeps exactly ONE crawler-visible
   * `<h1>` in the static HTML (both arms are styled the same, so the visitor's
   * arm still looks like a headline). On the client only one arm renders, so it
   * keeps the default `<h1>`.
   */
  asH1?: boolean;
}): React.ReactElement {
  const { shortName, gradeRange } = config;
  const isCompetition = variant === "competition-bridge";
  const HeroTag = asH1 ? "h1" : "p";
  // The literal keyword phrase (e.g. "STAAR Practice Test") carries the H1 —
  // Ahrefs confirmed it's the term people actually search, well above the
  // brand tagline. The tagline moves into the sub-copy below so it isn't lost.
  const seoPhrase = seoPhraseFor(config.slug, shortName);
  // Lead-magnet aware: when this test ships a free guide (lib/lead-magnets),
  // surface it next to the hero CTA. Pooled across all slugs — a test with no
  // magnet renders nothing here.
  const leadMagnet = leadMagnetForSlug(config.slug);

  const cta = (
    <>
      <div className="v-field__hero-ctas">
        <button
          type="button"
          className="btn-field-primary"
          style={{ color: "#fff" }}
          onClick={() => void diagnostic.start()}
          disabled={diagnostic.busy}
        >
          {diagnostic.busy ? "Starting…" : "Start Placement Test - Free"}
          <span className="arrow-bubble">
            <ArrowIcon size={16} />
          </span>
        </button>
      </div>
      {leadMagnet && (
        <p className="tl-lean__magnet">
          <span className="tl-lean__magnet-tag">Free gift</span>
          <span>
            Start the free placement and get our <strong>{leadMagnet.title}</strong>.
          </span>
        </p>
      )}
      <p className="v-field__hero-meta">
        <span className="dot" aria-hidden="true" />
        <span>{config.heroMeta}</span>
      </p>
    </>
  );

  // The hero copy + the bridge are the experiment's one axis; the fun-led grid,
  // the art collage, the CTA and the dark results band are held constant.
  const hero = isCompetition ? (
    <>
      <HeroTag className="v-field__hero-title">{seoPhrase}</HeroTag>
      <p className="v-field__hero-sub">
        The math game kids <span className="stripe">battle</span> to win — and it lifts their{" "}
        {shortName} score. Live 1-v-1 matches against other kids — your student climbs the ranks and
        beats their friends while drilling exactly what the {shortName} tests, for {gradeRange}.
      </p>
    </>
  ) : (
    <>
      <HeroTag className="v-field__hero-title">{seoPhrase}</HeroTag>
      <p className="v-field__hero-sub">
        The math game kids actually <span className="stripe">want</span> to play — and it improves
        their {shortName} score. Fast head-to-head math games your child will beg to keep playing —
        built on the skills the {shortName} tests, for {gradeRange}.
      </p>
    </>
  );

  const bridgeTitle = isCompetition ? (
    <>
      Compete in the game. <em>Results</em> on the {shortName}.
    </>
  ) : (
    <>
      Fun in the game. <em>Results</em> on the {shortName}.
    </>
  );

  const bridgeSteps: BridgeStep[] = isCompetition
    ? [
        {
          n: "01",
          title: "They go head-to-head",
          desc: "Live 60–90 second math battles against other kids — fast and real, nothing like a worksheet.",
        },
        {
          n: "02",
          title: "They climb the ranks",
          desc: "Winning climbs the leaderboard and earns bragging rights over friends, with questions that adapt to your student's level.",
        },
        {
          n: "03",
          title: `Scores climb on the ${shortName}`,
          desc: `Each battle drills the exact ${shortName} skills they're shakiest on, so the wins show up where it counts.`,
        },
      ]
    : [
        {
          n: "01",
          title: "They play",
          desc: "Quick 60–90 second math battles that feel like an arcade game, not a worksheet.",
        },
        {
          n: "02",
          title: "It adapts",
          desc: `Every match drills the exact ${shortName} skills your child is shakiest on, at their level.`,
        },
        {
          n: "03",
          title: "Scores climb",
          desc: `Skills build week over week and show up where it counts: on the ${shortName}.`,
        },
      ];

  return (
    <>
      <header className="v-field__hero tl-lean__hero">
        <div className="container">
          <div className="tl-lean__grid">
            <div>
              <span className="v-field__hero-tag">{config.heroTag}</span>
              {hero}
              {cta}
            </div>
            {isCompetition ? (
              <CompetitionBoard shortName={shortName} />
            ) : (
              <div className="tl-lean__visual">
                {GAME_SHOTS.map((shot, i) => (
                  <span key={shot.slug} className={`tl-lean__shot tl-lean__shot--${i + 1}`}>
                    {/* Img defaults to loading="lazy" — required: the SSR pass
                        stacks the other arm display:none for half of visitors, and
                        a hidden lazy image is never fetched. */}
                    <Img
                      src={`/game-assets/${shot.slug}/thumbnail.png`}
                      alt={`${shot.title} game`}
                      intent="card"
                      width={320}
                      height={200}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <section className="v-field__how tl-lean__bridge" aria-labelledby="tl-bridge-h">
        <div className="container">
          <div className="v-field__how-head">
            <span className="v-field__sec-eyebrow">How it works</span>
            <h2 id="tl-bridge-h" className="v-field__sec-title">
              {bridgeTitle}
            </h2>
          </div>
          <div className="v-field__steps">
            {bridgeSteps.map((step) => (
              <div key={step.n} className="v-field__step">
                <span className="v-field__step-num">{step.n}</span>
                <div className="v-field__step-body">
                  <h3 className="v-field__step-title">{step.title}</h3>
                  <p className="v-field__step-desc">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
